import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Mail, Phone, Clock3 } from "lucide-react";
import Seo from "../components/Seo";
import AssessmentWizard from "../components/AssessmentWizard";
import { KineticLines, Reveal, SectionLabel } from "../components/Reveal";
import { SERVICE_PAGES, serviceFromQuery } from "../lib/api";

export default function Contact() {
  const location = useLocation();
  const preset = useMemo(() => serviceFromQuery(new URLSearchParams(location.search).get("service")), [location.search]);
  const presetService = preset ? SERVICE_PAGES[preset] : null;

  return (
    <main data-testid="page-contact">
      <Seo
        title="Contact & Savings Review"
        description="Tell us about your business and explore savings opportunities — expenses, technology, benefits, and tax incentives. No cost to start, no obligation."
        path="/contact"
      />

      <section className="relative overflow-hidden bg-ice pt-28 lg:pt-36 pb-14 md:pb-20">
        <div className="absolute inset-0 bg-arch" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>Contact / Savings Review</SectionLabel>
            <KineticLines
              as="h1"
              className="mt-5 font-display font-extrabold tracking-tightest text-navy text-4xl sm:text-5xl leading-[1.06]"
              lines={["Let's Take a Smarter Look", <>at Your <span className="text-teal-dark">Bottom Line.</span></>]}
            />
            <Reveal delay={0.12}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-slate max-w-2xl">
                Four quick questions and a way to reach you — that's all it takes to get started. We'll review your
                interests and help you identify which specialist resources may be worth exploring.
              </p>
            </Reveal>
            {presetService && (
              <Reveal delay={0.2}>
                <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal-soft border border-teal/40 px-4 py-2 text-xs font-bold text-teal-dark" data-testid="contact-preselect-note">
                  We've pre-selected {presetService.short} from the page you came from — adjust as you like.
                </p>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <SectionLabel>What Happens Next</SectionLabel>
            <div className="mt-8 space-y-8">
              {[
                ["1", "You submit the review", "It lands securely in our system and the owner is notified of your inquiry."],
                ["2", "We review your interests", "A real person looks at what you selected and what may be relevant."],
                ["3", "A considered introduction", "If a specialist fits, we help connect you. You decide whether to continue — no pressure, ever."],
              ].map(([n, t, c]) => (
                <Reveal key={n}>
                  <div className="flex gap-5">
                    <span className="flex items-center justify-center w-11 h-11 rounded-2xl bg-navy text-teal-bright font-display font-extrabold shrink-0">{n}</span>
                    <div>
                      <h3 className="font-display font-extrabold tracking-tight text-navy text-lg">{t}</h3>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-slate">{c}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <div className="mt-10 rounded-2xl border border-line bg-ice/60 p-6 md:p-7" data-testid="contact-details-block">
                <h3 className="font-display font-extrabold tracking-tight text-navy">Prefer other ways to reach us?</h3>
                <ul className="mt-4 space-y-3 text-sm text-slate">
                  <li className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-teal-dark shrink-0" />
                    <a href="mailto:admin@communitypromotionsllc.com" data-testid="contact-email" className="hover:text-teal-dark transition-colors">admin@communitypromotionsllc.com</a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-teal-dark shrink-0" />
                    <a href="tel:+18643160078" data-testid="contact-phone" className="hover:text-teal-dark transition-colors">(864) 316-0078</a>
                  </li>
                  <li className="flex items-center gap-3"><Clock3 className="w-4 h-4 text-teal-dark" />Business hours: [to be added]</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <p className="mt-6 text-xs leading-relaxed text-slate-light">
                Bottom Line Improvements connects businesses with independent specialist providers and may receive
                referral compensation when customers choose services through participating provider relationships.
                Evaluations are performed by the specialists — not by Bottom Line Improvements.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <AssessmentWizard />
          </div>
        </div>
      </section>
    </main>
  );
}
